import { useEffect } from 'react';
import { Loader2, Unplug, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useGmailStore } from '../../store/gmailStore';
import type { SyncStatus, GmailConnectionItem } from '../../types';

const STATUS_BADGE: Record<SyncStatus, { label: string; color: string }> = {
  CONNECTED: {
    label: 'Connected',
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  SYNCING: {
    label: 'Syncing',
    color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
  ERROR: {
    label: 'Error',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  DISCONNECTED: {
    label: 'Disconnected',
    color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  },
};

const STATUS_ICON: Record<SyncStatus, typeof CheckCircle2> = {
  CONNECTED: CheckCircle2,
  SYNCING: RefreshCw,
  ERROR: AlertTriangle,
  DISCONNECTED: Unplug,
};

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ConnectionRow = ({
  connection,
  onDisconnect,
  isDisconnecting,
}: {
  connection: GmailConnectionItem;
  onDisconnect: (id: string) => void;
  isDisconnecting: boolean;
}) => {
  const badge = STATUS_BADGE[connection.syncStatus];
  const StatusIcon = STATUS_ICON[connection.syncStatus];
  const isActive = connection.isActive && connection.syncStatus !== 'DISCONNECTED';

  return (
    <tr className="border-b border-gray-100 dark:border-gray-700 last:border-0">
      <td className="px-4 py-3">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {connection.user.displayName || connection.user.email}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{connection.user.email}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">{connection.googleEmail}</p>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
          <StatusIcon className={`w-3 h-3 ${connection.syncStatus === 'SYNCING' ? 'animate-spin' : ''}`} />
          {badge.label}
        </span>
        {connection.syncStatus === 'ERROR' && connection.syncError && (
          <p className="text-xs text-red-500 dark:text-red-400 mt-1 truncate max-w-[200px]" title={connection.syncError}>
            {connection.syncError}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateTime(connection.lastSyncAt)}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        {isActive && (
          <button
            onClick={() => onDisconnect(connection.id)}
            disabled={isDisconnecting}
            className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium disabled:opacity-50 transition-colors"
          >
            Disconnect
          </button>
        )}
      </td>
    </tr>
  );
};

export const GmailConnectionsAdmin = () => {
  const {
    allConnections,
    isLoading,
    isDisconnecting,
    error,
    fetchAllConnections,
    disconnectUser,
  } = useGmailStore();

  useEffect(() => {
    fetchAllConnections();
  }, [fetchAllConnections]);

  const handleDisconnect = (connectionId: string) => {
    if (!confirm('Are you sure you want to disconnect this rep\'s Gmail? Their email sync will stop.')) {
      return;
    }
    disconnectUser(connectionId);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          All Gmail Connections
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Monitor and manage Gmail connections for all team members
        </p>
      </div>

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : allConnections.length === 0 ? (
        <div className="text-center py-8 px-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No Gmail connections yet. Reps can connect from their Settings page.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rep
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Gmail
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Sync
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {allConnections.map((conn) => (
                <ConnectionRow
                  key={conn.id}
                  connection={conn}
                  onDisconnect={handleDisconnect}
                  isDisconnecting={isDisconnecting}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
